import { applyCastAliasCleanupPass } from "@/lib/castAliasCleanupPass";
import type { BookCastJson, CastCharacter, CastNarrator } from "@/lib/castAnalysisTypes";
import type { CastChunkAnalysisOk, NarratorObservations } from "@/lib/castAnalysisChunkPayload";

function canonNameKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/^the\s+/i, "")
    .replace(/[''`]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function mergeUniqueStrings(parts: string[], maxLen: number): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const t = p.trim();
    if (t.length < 4) continue;
    const k = t.toLowerCase().slice(0, 120);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out.join(" · ").slice(0, maxLen);
}

function mergeNarratorFromObservations(obs: NarratorObservations[]): CastNarrator {
  const descriptions = obs.map((o) => o.description ?? "").filter(Boolean);
  const tones = obs.map((o) => o.tone ?? "").filter(Boolean);
  const dirs = obs.map((o) => o.voice_direction ?? "").filter(Boolean);
  return {
    name: "Narrator",
    description: mergeUniqueStrings(descriptions, 2400),
    tone: mergeUniqueStrings(tones, 1200),
    voice_direction: mergeUniqueStrings(dirs, 1200),
  };
}

type Agg = {
  displayName: string;
  key: string;
  chunkMentions: number;
  roles: string[];
  descriptions: string[];
  backstories: string[];
  personalities: string[];
  relationships: Set<string>;
  speakingStyles: string[];
  ages: string[];
  voiceDirs: string[];
  confidences: number[];
  sourceChunks: string[];
};

function pushAgg(agg: Agg, c: CastCharacter, chunkId: string) {
  agg.chunkMentions += 1;
  if (c.role?.trim()) agg.roles.push(c.role.trim());
  if (c.description?.trim()) agg.descriptions.push(c.description.trim());
  if (c.backstory?.trim()) agg.backstories.push(c.backstory.trim());
  if (c.personality?.trim()) agg.personalities.push(c.personality.trim());
  if (c.speaking_style?.trim()) agg.speakingStyles.push(c.speaking_style.trim());
  if (c.age_or_vocal_quality?.trim()) agg.ages.push(c.age_or_vocal_quality.trim());
  if (c.voice_direction?.trim()) agg.voiceDirs.push(c.voice_direction.trim());
  if (typeof c.confidence === "number" && Number.isFinite(c.confidence)) agg.confidences.push(c.confidence);
  agg.sourceChunks.push(chunkId);
  for (const r of c.relationships ?? []) {
    const t = r.trim();
    if (t) agg.relationships.add(t);
  }
}

/**
 * Merge map-phase chunk outputs into one canonical `BookCastJson` (dedupe + rank).
 */
export function mergeCastChunkAnalyses(
  meta: { bookId: string; title: string; author: string; source?: string },
  chunks: CastChunkAnalysisOk[],
): BookCastJson {
  const narratorObs: NarratorObservations[] = chunks.map((c) => c.narrator_observations);
  const narrator = mergeNarratorFromObservations(narratorObs);

  const byKey = new Map<string, Agg>();
  for (const ch of chunks) {
    for (const c of ch.characters) {
      const key = canonNameKey(c.name);
      if (!key) continue;
      let agg = byKey.get(key);
      if (!agg) {
        agg = {
          displayName: c.name.trim(),
          key,
          chunkMentions: 0,
          roles: [],
          descriptions: [],
          backstories: [],
          personalities: [],
          relationships: new Set(),
          speakingStyles: [],
          ages: [],
          voiceDirs: [],
          confidences: [],
          sourceChunks: [],
        };
        byKey.set(key, agg);
      } else {
        if (c.name.trim().length > agg.displayName.length) agg.displayName = c.name.trim();
      }
      pushAgg(agg, c, ch.chunk_id);
    }
  }

  const aggs = [...byKey.values()].sort((a, b) => {
    if (b.chunkMentions !== a.chunkMentions) return b.chunkMentions - a.chunkMentions;
    const maxA = Math.max(0, ...a.confidences, 0);
    const maxB = Math.max(0, ...b.confidences, 0);
    return maxB - maxA;
  });

  const characters: CastCharacter[] = aggs.map((a, rank) => {
    const rel = [...a.relationships].filter((x) => canonNameKey(x) !== a.key);
    const conf = a.confidences.length > 0 ? Math.min(1, Math.max(...a.confidences)) : undefined;
    const uniqRoles = [...new Set(a.roles)];
    const role = uniqRoles[0] ?? (rank === 0 ? "primary (inferred)" : "");
    return {
      name: a.displayName,
      role,
      description: mergeUniqueStrings(a.descriptions, 900),
      backstory: mergeUniqueStrings(a.backstories, 900),
      relationships: rel.slice(0, 24),
      personality: mergeUniqueStrings(a.personalities, 800),
      age_or_vocal_quality: mergeUniqueStrings(a.ages, 400),
      speaking_style: mergeUniqueStrings(a.speakingStyles, 500),
      voice_direction: mergeUniqueStrings(a.voiceDirs, 700),
      confidence: conf,
      voice: null,
      source_chunks: [...new Set(a.sourceChunks)].slice(0, 40),
      chunk_mentions: a.chunkMentions,
      importance_rank: rank + 1,
    };
  });

  return applyCastAliasCleanupPass({
    success: true,
    book_id: meta.bookId,
    title: meta.title,
    author: meta.author,
    source: meta.source,
    status: "ready",
    narrator,
    characters,
    analysis_mode: "full_book",
    chunks_analyzed: chunks.length,
  });
}
